/**
 * API Configuration Utilities
 * 
 * Centralized configuration for Visual Crossing API endpoints.
 * Supports server root override for testing, development, and load balancing.
 */

import { VcGlobal } from '/src/visualcrossing-core/vcglobal_conn';

// Default Visual Crossing API server
const DEFAULT_SERVER_ROOT = 'https://weather.visualcrossing.com';

// Global override (can be set at runtime)
let globalServerRootOverride = null;

/**
 * Set a global server root override
 * This affects all components that use getServerRoot()
 * 
 * @param {string|null} serverRoot - The server root URL, or null to clear override
 */
export function setGlobalServerRoot(serverRoot) {
  globalServerRootOverride = serverRoot ? serverRoot.replace(/\/$/, '') : null;
}

/**
 * Get the current global server root override
 * 
 * @returns {string|null} The current override, or null if none set
 */
export function getGlobalServerRootOverride() {
  return globalServerRootOverride;
}

/**
 * Get the server root URL to use for API calls
 * 
 * Priority order:
 * 1. Component-level override (if provided)
 * 2. Global override (if set via setGlobalServerRoot)
 * 3. VcGlobal.SERVER_ROOT (if available)
 * 4. Default server root
 * 
 * @param {string} [componentOverride] - Optional component-specific override
 * @returns {string} The server root URL (without trailing slash)
 */
export function getServerRoot(componentOverride = null) {
  // 1. Component-level override takes highest priority
  if (componentOverride) {
    return componentOverride.replace(/\/$/, '');
  }
  
  // 2. Global override
  if (globalServerRootOverride) {
    return globalServerRootOverride;
  }
  
  // 3. VcGlobal.SERVER_ROOT
  if (VcGlobal && VcGlobal.SERVER_ROOT) {
    return VcGlobal.SERVER_ROOT.replace(/\/$/, '');
  }
  
  // 4. Default
  return DEFAULT_SERVER_ROOT;
}

/**
 * Build a Timeline API URL
 * 
 * @param {string} location - Location string
 * @param {Object} options - URL options
 * @param {string} [options.startDate] - Start date (YYYY-MM-DD)
 * @param {string} [options.endDate] - End date (YYYY-MM-DD)
 * @param {string} [options.unitGroup='us'] - Unit group
 * @param {string} [options.include] - Include sections (e.g., 'days,hours')
 * @param {string} [options.elements] - Elements to include
 * @param {string} [options.apiKey] - API key
 * @param {string} [options.serverRoot] - Server root override
 * @returns {string} The full API URL
 */
export function buildTimelineUrl(location, options = {}) {
  const {
    startDate,
    endDate,
    unitGroup = 'us',
    include,
    elements,
    apiKey,
    serverRoot
  } = options;
  
  const baseUrl = getServerRoot(serverRoot);
  const encodedLocation = encodeURIComponent(location);
  
  let url = `${baseUrl}/VisualCrossingWebServices/rest/services/timeline/${encodedLocation}`;
  
  // Add date range if provided
  if (startDate) {
    url += `/${startDate}`;
    if (endDate) {
      url += `/${endDate}`;
    }
  }
  
  // Build query parameters
  const params = new URLSearchParams();
  params.set('unitGroup', unitGroup);
  
  if (include) params.set('include', include);
  if (elements) params.set('elements', elements);
  if (apiKey) params.set('key', apiKey);
  
  return `${url}?${params.toString()}`;
}

/**
 * Build a Geo API URL
 * 
 * @param {string} endpoint - The geo endpoint ('geocode', 'reverse', 'route')
 * @param {Object} params - Query parameters
 * @param {string} [serverRoot] - Server root override
 * @returns {string} The full API URL
 */
export function buildGeoUrl(endpoint, params = {}, serverRoot = null) {
  const baseUrl = getServerRoot(serverRoot);
  const url = `${baseUrl}/VisualCrossingWebServices/rest/api/v1/geo/${endpoint}`;
  
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      searchParams.set(key, String(value));
    }
  }
  
  return `${url}?${searchParams.toString()}`;
}

/**
 * Default export with all utilities
 */
export default {
  getServerRoot,
  setGlobalServerRoot,
  getGlobalServerRootOverride,
  buildTimelineUrl,
  buildGeoUrl,
  DEFAULT_SERVER_ROOT
};
